<template>
  <div class="export-wrapper">
    <div class="export-group">
      <p class="export-label">Create with 1-click integration</p>

      <!-- Premium Feature Card when premium integrations entitlement is disabled -->
      <PremiumFeatureCard
        v-if="!hasAccess"
        title="1-click integrations"
        description="Be able to instantly export content like videos and screenshots to connected platforms such as Notion, Jira, or Zendesk."
        @upgrade="handleUpgrade"
        class="mb-4"
      />

      <!-- Setup message when premium integrations entitlement is enabled but no integrations are connected -->
      <div
        v-else-if="hasAccess && !hasEnabledIntegrations"
        class="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg"
      >
        <p class="text-sm text-blue-800 dark:text-blue-300">
          No integrations are connected yet.
          <a :href="`${baseUrl}/integrations`" class="underline font-medium hover:text-blue-600 dark:hover:text-blue-200"> Set up your integrations </a>
          to enable 1-click export.
        </p>
      </div>

      <!-- Integration Buttons -->
      <div v-if="allIntegrations.length" class="export-buttons">
        <Button
          v-for="integration in allIntegrations"
          type="ghost"
          variant="black"
          :key="integration.name"
          :icon="integration.icon"
          :disabled="!hasAccess || !integration.enabled"
          :class="{ 'opacity-50 cursor-not-allowed': !hasAccess || !integration.enabled }"
          data-testid="viewer-ai-workflow-integration"
          @click.prevent="handleIntegrationClick(integration)"
          >{{ generateLabel(integration.name) }}</Button
        >
      </div>
      <McpContextNotice v-if="shouldShowMcpNotice" placement="export_modal" />
      <HorizontalDivider />
    </div>
    <div class="export-group">
      <p class="export-label">Copy generated content</p>
      <div class="export-buttons">
        <Button type="ghost" variant="black" icon="general.transcription" text="Copy text" data-testid="copy-text" @click.prevent="sendToClipboard" />
        <Button type="ghost" variant="black" icon="general.downloadText" text="Download as PDF" data-testid="download-pdf" @click.prevent="sendToPdf" />
        <Button type="ghost" variant="black" icon="exports.googleDocs" text="Google Docs" data-testid="google-docs" @click.prevent="sendToGoogleDocs" />
        <Button type="ghost" variant="black" icon="exports.googleGmail" text="Gmail" data-testid="gmail" @click.prevent="sendToGmail" />
        <Button type="ghost" variant="black" icon="exports.microsoftWord" text="Microsoft Word" data-testid="ms-word" @click.prevent="sendToWord" />
        <Button type="ghost" variant="black" icon="exports.notion" text="Notion" data-testid="notion" @click.prevent="sendToNotion" />
        <Button
          type="ghost"
          variant="black"
          icon="exports.confluence"
          text="Confluence / Jira"
          data-testid="confluence-jira"
          @click.prevent="sendToConfluenceJira"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Button, HorizontalDivider } from '@/storybook';
import { PremiumFeatureCard } from '@/storybook/templates/limits';
import { ItemType, useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { useVideoAi } from '@/modules/viewer/composables/useVideoAi';
import { computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useToast } from '@/composables/useToast';
import { useExportUtils } from '@/modules/viewer/composables/useExportUtils';
import { TrackEvents, useViewerTrack } from '@/modules/viewer/composables/useViewerTrack';
import { useIntegrationStore, type Integration } from '@/modules/dashboard/integrations/store/useIntegrationStore';
import { TrackClickEvents, useTrack } from '@/composables/useTrack';
import { useEntitlementsStore } from '@/store/useEntitlementsStore';
import McpContextNotice from '@/modules/viewer/components/McpContextNotice.vue';

const itemStore = useViewerItemStore();
const { description, displayName, item } = storeToRefs(itemStore);
const { hasAiAddon } = useVideoAi();
const integrationsStore = useIntegrationStore();
const entitlementsStore = useEntitlementsStore();
const { success, error } = useToast();
const { copyConfluenceMarkdown, copyMarkdownWithoutImages, copyHtmlFragment, downloadPdf, copyWithVideoLink } = useExportUtils(description, displayName);
const { trackItemOwner } = useViewerTrack();
const { trackClick } = useTrack();

// Fallback workflow integrations for when API fails (local dev without Paragon credentials)
const FALLBACK_WORKFLOW_INTEGRATIONS = [
  { name: 'Intercom', enabled: false, icon: '', color: '', description: '' },
  { name: 'Zendesk', enabled: false, icon: '', color: '', description: '' },
  { name: 'Confluence', enabled: false, icon: '', color: '', description: '' },
  { name: 'Jira', enabled: false, icon: '', color: '', description: '' },
  { name: 'Notion', enabled: false, icon: '', color: '', description: '' },
];

// Show integrations from API, or fallback list if API fails
const allIntegrations = computed(() => {
  const integrations = integrationsStore.integrations.length > 0 ? integrationsStore.integrations : FALLBACK_WORKFLOW_INTEGRATIONS;
  const canAccess = entitlementsStore.can('premiumIntegrations');

  return integrations.map(integration => ({
    ...integration,
    // Override enabled state if no access - all should be disabled
    enabled: canAccess ? integration.enabled : false,
  }));
});

// Whether the current org has the premium integrations entitlement
const hasAccess = computed(() => {
  return entitlementsStore.can('premiumIntegrations');
});

const shouldShowMcpNotice = computed(() => hasAiAddon.value && item.value?.attributes?.itemType === ItemType.VIDEO);

// Check if any integrations are actually enabled/connected
const hasEnabledIntegrations = computed(() => {
  return allIntegrations.value.some(integration => integration.enabled);
});

const baseUrl = window.location.origin;

const sendTo = (integration: string) => {
  integrationsStore.sendTo(integration);
  success(`Your request has been queued. Please check ${integration} dashboard for updates.`);
};

const handleIntegrationClick = (integration: Integration) => {
  if (!hasAccess.value) {
    error('This feature requires an upgrade. Please upgrade to continue.');
    return;
  }
  if (!integration.enabled) {
    error('This integration is not enabled. Please enable it in your integrations settings.');
    return;
  }
  sendTo(integration.name);
};

const handleUpgrade = () => {
  const baseUrl = window.location.origin;
  window.location.href = `${baseUrl}/account/plans`;
};

onMounted(() => {
  trackClick(TrackClickEvents.VIEWER_EXPORT_TAB);
  integrationsStore.fetchIntegrations();
});

function withTracking(tool: string, fn: () => void) {
  return () => {
    trackItemOwner(TrackEvents.SMARTACTIONS_SEND_TO, { tool: tool });
    fn();
  };
}

function generateLabel(integrationName: string): string {
  switch (integrationName.toLowerCase()) {
    case 'zendesk':
      return 'Zendesk article';
    case 'jira':
      return 'Jira Ticket';
    case 'confluence':
      return 'Confluence page';
    case 'notion':
      return 'Notion page';
    default:
      return integrationName;
  }
}

const sendToClipboard = withTracking('clipboard', copyWithVideoLink);
const sendToPdf = withTracking('pdf', downloadPdf);
const sendToGoogleDocs = withTracking('google_docs', copyHtmlFragment);
const sendToGmail = withTracking('gmail', copyHtmlFragment);
const sendToWord = withTracking('word', copyHtmlFragment);
const sendToNotion = withTracking('notion', copyMarkdownWithoutImages);
const sendToConfluenceJira = withTracking('confluence', copyConfluenceMarkdown);
</script>

<style scoped>
.export-wrapper {
  @apply flex flex-col gap-4;
}
.export-group {
  @apply flex flex-col gap-4;
}
.export-label {
  @apply text-sm font-semibold text-slate-500 dark:text-zinc-300;
}
.export-buttons {
  @apply flex flex-wrap gap-4;
}
</style>
